import React, {useState} from 'react';
import ArrModal from '../../../common/arr-modal/ArrModal';
import {Box, Typography} from '@mui/material';
import ArrButton from '../../../common/arr-button/ArrButton';
import {t} from 'i18next';
import './ConfirmModal.scss';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import dayjs from 'dayjs';

interface IConfirmModal {
  isOpen: boolean;
  onClose: () => void;
  selectedDocumentCount: number | '--';
  setShowToast: (value: boolean) => void;
  handleSubmit: () => void;
}

const ConfirmModal = (props: IConfirmModal) => {
  const {isOpen, onClose, selectedDocumentCount, setShowToast, handleSubmit} =
    props;
  const [textboxValue, setTextboxValue] = useState<string>('');
  const isConfirmDisabled =
    textboxValue.trim().replace(/\s+/g, ' ') !== 'I understand';
  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );
  const handleConfirm = () => {
    handleSubmit();
  };
  const now = dayjs();

  const name = loginUserData?.userName || '';
  const time = now.format('YYYY/MM/DD [at] hh:mm:ss A');
  const modal_sub_content_style = `typo-text body2_size ${isConfirmDisabled ? 'modal-text-color' : 'modal-text-color-confirm'}`;
  return (
    <div
      className="ai-summarize-confirm-modal"
      data-testid={`confirm-modal-selected-documents-agreement-cancel-confirm`}
    >
      <ArrModal
        showModal={isOpen}
        onClose={onClose}
        className="ai-summarize-confirm-modal"
        showClose={false}
        dataTestId="ai-summarize-confirm-modal"
        headerTitleElement={
          isConfirmDisabled ? (
            <Typography
              className="typo-text headline3_size modal-text-color"
              data-testid="flyout-title"
              tabindex={0}
            >
              {t('ai.summarize.createJob.confirmModal.header.newJob')}
            </Typography>
          ) : (
            <Box className="modal-header">
              <Typography
                className="typo-text headline2_size modal-text-color"
                data-testid="flyout-title"
              >
                {t('ai.summarize.createJob.confirmModal.header.update')}
              </Typography>

              <ArrTooltipWrapper
                data-testid="select-document-tooltip-wrapper"
                aria-label={t(
                  'ai.summarize.createJob.confirmModal.header.tooltip'
                )}
                trigger={
                  <InfoIcon
                    tabIndex={0}
                    aria-label={t(
                      'ai.summarize.createJob.confirmModal.header.tooltip'
                    )}
                    className="info-icon"
                    data-testid="info-icon"
                  />
                }
              >
                {t('ai.summarize.createJob.confirmModal.header.tooltip')}
              </ArrTooltipWrapper>
            </Box>
          )
        }
      >
        <Box className="flyout">
          <Box className="modal-sub-content">
            <Typography
              tabindex={0}
              className={modal_sub_content_style}
              data-testid={`confirm-modal-typography`}
            >
              {isConfirmDisabled
                ? t('ai.summarize.createJob.confirmModal.description.summarize')
                : t(
                    'ai.summarize.createJob.confirmModal.description.updateIndex'
                  )}
            </Typography>
          </Box>

          <Box className="modal-sub-content">
            <Typography
              tabindex={0}
              className={modal_sub_content_style}
              data-testid={`confirm-modal-selected-documents`}
            >
              {t('ai.summarize.createJob.confirmModal.selectedDocuments')}{' '}
              <span
                className="typo-text subhead4_size"
                data-testid={`confirm-modal-span`}
              >
                {selectedDocumentCount}
              </span>
            </Typography>
          </Box>

          <Box className="modal-sub-content">
            <Typography
              tabindex={0}
              className={modal_sub_content_style}
              data-testid={`confirm-modal-agreement`}
            >
              {t('ai.summarize.createJob.confirmModal.agreement')}
            </Typography>
          </Box>

          <Box className="modal-content-textbox">
            <ArrTextField
              readerrorText
              sizeVariant="large"
              state={
                isConfirmDisabled && textboxValue !== '' ? 'error' : 'enabled'
              }
              showErrorIcon
              supportText={
                textboxValue === ''
                  ? ''
                  : isConfirmDisabled
                    ? t('ai.summarize.createJob.confirmModal.supportTextError')
                    : t(
                        'ai.summarize.createJob.confirmModal.supportTextSignature',
                        {name, time}
                      )
              }
              className="search-input-modal"
              label={t('ai.summarize.createJob.confirmModal.typePlaceholder', {
                doubleQuote: '"',
              })}
              aria-label={t(
                'ai.summarize.createJob.confirmModal.typePlaceholder'
              )}
              value={textboxValue}
              onChange={(e) => {
                setTextboxValue(e.target.value);
              }}
              dataTestId="confirm-modal-search-input"
            />
          </Box>

          <Box className="modal-action-buttons">
            <ArrButton
              variant="secondary"
              size="medium"
              onClick={onClose}
              aria-label={t('common.cancel')}
              dataTestId="confirm-modal-cancel-button"
              className="modal-action-button"
            >
              {t('common.cancel')}
            </ArrButton>

            <ArrButton
              isAriaDisabled={isConfirmDisabled}
              variant="primary"
              size="medium"
              type="submit"
              aria-label={t('ai.summarize.createJob.confirmModal.confirm')}
              dataTestId="confirm-modal-confirm-button"
              onClick={handleConfirm}
              className="modal-action-button"
            >
              {t('ai.summarize.createJob.confirmModal.confirm')}
            </ArrButton>
          </Box>
        </Box>
      </ArrModal>
    </div>
  );
};

export default ConfirmModal;
